import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

/* -------- Impersonation state (platform_owner only) -------- */
const IMP_KEY = "wfos_impersonate";

export function getImpersonation() {
    try {
        const raw = sessionStorage.getItem(IMP_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

export function setImpersonation(payload) {
    if (payload) sessionStorage.setItem(IMP_KEY, JSON.stringify(payload));
    else sessionStorage.removeItem(IMP_KEY);
    window.dispatchEvent(new CustomEvent("wfos:impersonation-change"));
}

/* -------- axios interceptor -------- */
api.interceptors.request.use((config) => {
    const t = localStorage.getItem("wfos_token");
    if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
    }
    // Sprint J-3 · stamp every request with a client-side X-Request-ID.
    // The backend uses this to make record_usage() upserts idempotent, so
    // network retries and StrictMode double-invocations never double-count.
    if (!config.headers["X-Request-ID"]) {
        config.headers = config.headers || {};
        config.headers["X-Request-ID"] = crypto?.randomUUID?.()
            || `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    const imp = getImpersonation();
    if (imp?.company_id) {
        config.headers = config.headers || {};
        config.headers["X-Impersonate-Company"] = imp.company_id;
    }
    return config;
});

/* Sprint J-3 · auto-open PaywallModal on 402. Any component can also
   dispatch `wfos:paywall` manually with a custom detail if it wants to
   surface the modal preemptively. */
api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 402) {
            const raw = err.response.data?.detail ?? err.response.data ?? {};
            const payload = typeof raw === "object" && raw !== null ? raw : { message: String(raw) };
            payload._request_url = err.config?.url;
            window.dispatchEvent(new CustomEvent("wfos:paywall", { detail: payload }));
            // Replace `.detail` with a plain string so downstream `toast.error(detail)`
            // callers (which are common across the app) don't crash trying to render
            // the structured payload as a React child. The paywall event above still
            // carries the full object.
            if (err.response.data && typeof err.response.data === "object") {
                err.response.data = {
                    ...err.response.data,
                    detail: payload.message || payload.error || "Upgrade required.",
                    paywall: payload,
                };
            }
        }
        return Promise.reject(err);
    }
);

export function resolveAvatar(url) {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
}

/* -------- Streaming helper (SSE reader) -------- */
export async function streamSSE(path, body, { onStart, onDelta, onDone, onError } = {}) {
    const token = localStorage.getItem("wfos_token");
    const imp = getImpersonation();
    // Sprint J-3 · pass a stable per-mutation X-Request-ID so retries don't
    // double-count on the server side (usage_meter upserts on request_id).
    const requestId = (crypto?.randomUUID?.() || `req-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const headers = {
        "Content-Type": "application/json",
        "X-Request-ID":  requestId,
        Authorization: `Bearer ${token}`,
    };
    if (imp?.company_id) headers["X-Impersonate-Company"] = imp.company_id;

    const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
        const parsed = await res.json().catch(() => ({}));
        // Sprint J-3 · SSE paywalls — fetch-path 402s must also open the modal,
        // matching the axios interceptor behavior.
        if (res.status === 402) {
            const detail = parsed?.detail ?? parsed;
            const paywall = typeof detail === "object" && detail !== null ? detail : { message: String(detail) };
            paywall._request_url = path;
            window.dispatchEvent(new CustomEvent("wfos:paywall", { detail: paywall }));
            const e = new Error(paywall.message || paywall.error || "Upgrade required.");
            e.paywall = paywall;
            onError?.(e);
            throw e;
        }
        const msg = parsed?.detail?.message || (typeof parsed?.detail === "string" ? parsed.detail : `HTTP ${res.status}`);
        throw new Error(msg);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let final = "";
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop();
        for (const c of chunks) {
            const line = c.trim();
            if (!line.startsWith("data:")) continue;
            const payloadStr = line.slice(5).trim();
            if (!payloadStr) continue;
            let payload;
            try { payload = JSON.parse(payloadStr); } catch { continue; }
            if (payload.type === "start") onStart?.(payload);
            else if (payload.type === "delta") {
                final += payload.content || "";
                onDelta?.(payload.content || "", final);
            } else if (payload.type === "done") {
                final = payload.content || final;
                onDone?.(final, payload);
                return final;
            } else if (payload.type === "error") {
                const e = new Error(payload.message);
                onError?.(e);
                throw e;
            }
        }
    }
    return final;
}

export default api;
